// src/components/admin/AddressMiniMapDisplay.tsx
import React from 'react';
import { MapPin, Phone, Navigation } from 'lucide-react';

interface AddressMiniMapDisplayProps {
  address: {
    house_number?: string;
    address_line_1?: string;
    address_line_2?: string;
    city?: { name: string };
    city_name?: string;
    latitude?: string | number;
    longitude?: string | number;
    contact_number_1?: string;
    delivery_note?: string;
  };
}

const AddressMiniMapDisplay: React.FC<AddressMiniMapDisplayProps> = ({ address }) => {
  const hasCoordinates = address?.latitude && address?.longitude;
  const lat = hasCoordinates ? Number(address.latitude) : 0;
  const lng = hasCoordinates ? Number(address.longitude) : 0;
  
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <h3 className="font-semibold text-gray-900 mb-4">Business Address</h3>
      <div className="flex flex-col md:flex-row gap-4">
        {/* Left - Address Details */}
        <div className="flex-1 space-y-2">
          <div className="flex items-start gap-2">
            <MapPin className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
            <div>
              {(address?.house_number || address.address_line_1) && (
                <p className="text-gray-900">{address.house_number} {address.address_line_1}</p>
              )}
              {address?.address_line_2 && (
                <p className="text-gray-900">{address.address_line_2}</p>
              )}
              <p className="text-gray-900">
                {address?.city?.name || address?.city_name || address?.city_details.name|| 'City not specified'}
              </p>
            </div>
          </div>
          {address?.delivery_note && (
            <div className="mt-2 p-2 bg-gray-50 rounded text-sm text-gray-600">
              <p className="text-xs text-gray-500">Delivery Note:</p>
              <p>{address.delivery_note}</p>
            </div>
          )}
        </div>

        {/* Right - Mini Map */}
        <div className="w-full md:w-64 flex-shrink-0">
          {hasCoordinates ? (
            <div className="relative group rounded-lg overflow-hidden border">
              <iframe
                title="Location Map"
                width="100%"
                height="180"
                frameBorder="0"
                src={`https://www.openstreetmap.org/export/embed.html?bbox=${lng - 0.01},${lat - 0.01},${lng + 0.01},${lat + 0.01}&layer=mapnik&marker=${lat},${lng}`}
                className="w-full"
              />
              <a
                href={`https://www.google.com/maps?q=${lat},${lng}`}
                target="_blank"
                rel="noopener noreferrer"
                className="absolute bottom-1 right-1 bg-white/90 text-xs px-2 py-1 rounded shadow hover:bg-white transition-colors"
              >
                Open in Maps
              </a>
            </div>
          ) : (
            <div className="h-44 bg-gray-100 rounded-lg border flex items-center justify-center">
              <div className="text-center">
                <Navigation className="h-8 w-8 text-gray-300 mx-auto mb-1" />
                <p className="text-xs text-gray-500">No location data</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddressMiniMapDisplay;